// member-portal/src/components/SuperAdminBillingPortal.js — FULL FILE with defensive grandTotal rendering

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './SuperAdminBillingPortal.css';

const SuperAdminBillingPortal = ({ navigateTo }) => {
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [genMessage, setGenMessage] = useState('');
  const [approveLoading, setApproveLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() - 1);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  useEffect(() => {
    fetchCycles();
  }, []);

  const fetchCycles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('memberToken');
      const res = await axios.get(`${API_URL}/api/superadmin/billing/cycles?forceFresh=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCycles(res.data);
    } catch (err) {
      console.error('Failed to load cycles:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectCycle = async (cycleId) => {
    setSelectedCycle(cycleId);
    setLoading(true);
    try {
      const token = localStorage.getItem('memberToken');
      const res = await axios.get(`${API_URL}/api/superadmin/billing/cycle/${cycleId}?forceFresh=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(res.data.invoices || []);
    } catch (err) {
      console.error('Failed to load invoices:', err);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const reviewInvoice = (invoiceId, orgId) => {
    navigateTo('invoiceEditor', { invoiceId, orgId });
  };

  const markAsPaid = async (invoiceId) => {
    if (!window.confirm('Mark this invoice as paid?')) return;

    try {
      const token = localStorage.getItem('memberToken');
      await axios.patch(`${API_URL}/api/superadmin/billing/invoice/${invoiceId}/paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setInvoices(prev => prev.map(inv =>
        inv.id === invoiceId ? { ...inv, paid: true, paidAt: new Date().toISOString() } : inv
      ));
      toast.success('Invoice marked as paid');
    } catch (err) {
      console.error('Failed to mark as paid:', err);
      toast.error('Failed to update payment status');
    }
  };

  const handleManualGenerate = async () => {
    setGenLoading(true);
    setGenMessage('');
    try {
      const token = localStorage.getItem('memberToken');
      const response = await axios.post(`${API_URL}/api/superadmin/billing/manual-generate`, { year, month }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGenMessage(response.data.message);
      await fetchCycles();
      toast.success('Invoices generated successfully');
    } catch (err) {
      setGenMessage(err.response?.data?.message || 'Error generating invoices');
      toast.error('Failed to generate invoices');
    }
    setGenLoading(false);
  };

  const handleApproveAll = async () => {
    if (!selectedCycle) return;

    if (!window.confirm('Approve ALL invoices for this billing cycle?\n\nThis will set status to "approved" for every invoice in the cycle. This action cannot be undone.')) {
      return;
    }

    setApproveLoading(true);
    try {
      const token = localStorage.getItem('memberToken');
      const response = await axios.patch(
        `${API_URL}/api/superadmin/billing/cycle/${selectedCycle}/approve-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      await selectCycle(selectedCycle);
      await fetchCycles();
    } catch (err) {
      console.error('Bulk approve failed:', err);
      toast.error(err.response?.data?.message || 'Failed to approve all invoices');
    } finally {
      setApproveLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-ZA');
  };

  return (
    <div className="billing-portal">
      <h1>Billing Portal</h1>
      <p className="subtitle">
        Professional monthly billing — review, edit, credit, approve, and email invoices.
      </p>

      <div className="manual-generate">
        <h3>Manual Invoice Generation</h3>
        <div className="form-row">
          <div>
            <label>Year</label>
            <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value))} />
          </div>
          <div>
            <label>Month (0=Jan, 1=Feb...)</label>
            <input type="number" min="0" max="11" value={month} onChange={(e) => setMonth(parseInt(e.target.value))} />
          </div>
          <button onClick={handleManualGenerate} disabled={genLoading || loading}>
            {genLoading ? 'Generating...' : 'Generate Invoices'}
          </button>
        </div>
        {genMessage && (
          <p className={`gen-message ${genMessage.includes('complete') ? 'success' : 'error'}`}>
            {genMessage}
          </p>
        )}
      </div>

      <div className="cycles-grid">
        {cycles.map(cycle => {
          const displayAmount = typeof cycle.totalAmount === 'number'
            ? cycle.totalAmount.toFixed(2)
            : Number(cycle.totalAmount ?? 0).toFixed(2);

          return (
            <div
              key={cycle.id || cycle._id}
              className={`cycle-card ${selectedCycle === (cycle.id || cycle._id) ? 'selected' : ''}`}
              onClick={() => selectCycle(cycle.id || cycle._id)}
            >
              <h3>{cycle.period}</h3>
              <p><strong>{cycle.invoiceCount || 0} invoices</strong></p>
              <p className="total">R{displayAmount}</p>
            </div>
          );
        })}
      </div>

      {selectedCycle && (
        <div className="invoices-section">
          <div className="section-header">
            <h2>Invoices</h2>
            {invoices.length > 0 && (
              <button
                onClick={handleApproveAll}
                disabled={approveLoading || loading}
                className="approve-all-btn"
              >
                {approveLoading ? 'Approving...' : 'Approve All Invoices'}
              </button>
            )}
          </div>

          {invoices.length === 0 ? (
            <p className="no-data">No invoices found for this period</p>
          ) : (
            <table className="invoices-table">
              <thead>
                <tr>
                  <th>Restaurant</th>
                  <th>Impressions</th>
                  <th>Views</th>
                  <th>Clicks</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Paid</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  // Defensive fix: handle grandTotal as number or string or null
                  const displayTotal = typeof inv.grandTotal === 'number'
                    ? inv.grandTotal.toFixed(2)
                    : Number(inv.grandTotal ?? 0).toFixed(2);

                  return (
                    <tr key={inv.id}>
                      <td>{inv.orgName}</td>
                      <td>{inv.impressions.toLocaleString()}</td>
                      <td>{inv.views.toLocaleString()}</td>
                      <td>{inv.clicks.toLocaleString()}</td>
                      <td className="total">R{displayTotal}</td>
                      <td>{inv.status}</td>
                      <td>
                        {inv.paid ? (
                          <span style={{ color: 'green' }}>✓ Paid {formatDate(inv.paidAt)}</span>
                        ) : (
                          <span style={{ color: 'orange' }}>Pending</span>
                        )}
                      </td>
                      <td className="actions-cell">
                        <button onClick={() => reviewInvoice(inv.id, inv.orgId)} className="review-btn">
                          Review
                        </button>
                        {!inv.paid && (
                          <button
                            onClick={() => markAsPaid(inv.id)}
                            className="review-btn mark-paid"
                          >
                            Mark Paid
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default SuperAdminBillingPortal;